import type { Appointment } from '@dnx/contracts';
import { Button, cn } from '@dnx/ui';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { BellRing, CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { meQuery } from '../lib/session.js';
import { AppointmentDrawer } from './appointment-drawer.js';
import { addDays, calendarQuery, hhmm, KIND_LABEL, localMinutes, longDate, professionalsQuery, STATUS_STYLE, todayLocal } from './queries.js';

export interface AgendaSearch { dia?: string; consulta?: string }
const route = getRouteApi('/app/agenda');

const START_H = 7;
const END_H = 20;
const PX_PER_MIN = 1.1; // 66 px por hora

function Block({ a, onOpen }: { a: Appointment; onOpen: () => void }) {
  const top = (localMinutes(a.startsAt) - START_H * 60) * PX_PER_MIN;
  const height = Math.max(28, (new Date(a.endsAt).getTime() - new Date(a.startsAt).getTime()) / 60_000 * PX_PER_MIN - 2);
  const st = STATUS_STYLE[a.status]!;
  return (
    <button type="button" onClick={onOpen} style={{ top, height }}
      className={cn('absolute inset-x-1 overflow-hidden rounded-md border-l-4 px-2 py-1 text-left text-[12px] shadow-card transition-shadow hover:shadow-pop', st.box)}>
      <span className="flex items-center justify-between gap-1">
        <span className="truncate font-semibold">{a.contact?.name ?? 'Bloqueio'}</span>
        {(a.reminders.h48 || a.reminders.h24) && <BellRing className="size-3 shrink-0 opacity-60" aria-label="Lembrete enviado" />}
      </span>
      <span className="block truncate opacity-80 tabular-nums">{hhmm(a.startsAt)} · {KIND_LABEL[a.kind]} · {st.label}</span>
      {a.confirmation === 'reschedule_requested' && <span className="block font-medium text-warning">Pediu para remarcar</span>}
    </button>
  );
}

export function AgendaPage() {
  const { dia, consulta } = route.useSearch();
  const navigate = useNavigate();
  const date = dia ?? todayLocal();
  const { data: me } = useSuspenseQuery(meQuery);
  const canWrite = me.permissions.includes('scheduling.appointment.write');
  const { data: pros } = useQuery(professionalsQuery);
  const { data: appts } = useQuery(calendarQuery(date));
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 60_000); return () => clearInterval(t); }, []);
  const go = (s: Partial<AgendaSearch>) => void navigate({ to: '/agenda', search: { dia, consulta, ...s }, replace: true });

  const visible = (appts ?? []).filter((a) => a.status !== 'cancelled');
  const selected = appts?.find((a) => a.id === consulta) ?? null;
  const isToday = date === todayLocal();
  const nowTop = (localMinutes(new Date(now).toISOString()) - START_H * 60) * PX_PER_MIN;
  const summary = {
    total: visible.filter((a) => a.kind !== 'block').length,
    confirmed: visible.filter((a) => a.status === 'confirmed' || a.status === 'checked_in' || a.status === 'completed').length,
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex flex-wrap items-center gap-3 border-b border-border bg-surface px-4 py-4 sm:px-8">
        <div className="mr-auto">
          <h1 className="text-xl font-semibold tracking-tight text-fg first-letter:uppercase">{longDate(date)}</h1>
          <p className="text-[13px] text-fg-muted">{summary.total} {summary.total === 1 ? 'consulta' : 'consultas'} · {summary.confirmed} confirmadas</p>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="secondary" size="icon" aria-label="Dia anterior" onClick={() => go({ dia: addDays(date, -1), consulta: undefined })}><ChevronLeft className="size-4" /></Button>
          <Button variant="secondary" onClick={() => go({ dia: undefined, consulta: undefined })} disabled={isToday}>Hoje</Button>
          <Button variant="secondary" size="icon" aria-label="Próximo dia" onClick={() => go({ dia: addDays(date, 1), consulta: undefined })}><ChevronRight className="size-4" /></Button>
          <input type="date" aria-label="Ir para a data" value={date} onChange={(e) => e.target.value && go({ dia: e.target.value, consulta: undefined })}
            className="ml-2 h-10 rounded-md border border-border-strong bg-surface px-2 text-sm text-fg" />
        </div>
      </header>

      {pros && pros.length === 0 ? (
        <div className="grid flex-1 place-items-center bg-bg px-6 text-center">
          <div>
            <span className="mx-auto grid size-12 place-items-center rounded-full bg-primary-soft text-primary"><CalendarDays className="size-6" /></span>
            <p className="mt-3 text-sm font-medium text-fg">Nenhum profissional com agenda</p>
            <p className="mt-1 max-w-sm text-[12.5px] text-fg-muted">Cadastre o horário de atendimento dos médicos para começar a agendar avaliações.</p>
          </div>
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-auto bg-bg">
          <div className="flex min-w-full">
            {/* régua de horas */}
            <div className="sticky left-0 z-10 w-14 shrink-0 border-r border-border bg-bg pt-11">
              {Array.from({ length: END_H - START_H }, (_, i) => (
                <div key={i} style={{ height: 60 * PX_PER_MIN }} className="relative">
                  <span className="absolute -top-2 right-2 text-[11px] text-fg-subtle tabular-nums">{String(START_H + i).padStart(2, '0')}:00</span>
                </div>
              ))}
            </div>
            {pros?.map((p) => {
              const weekday = new Date(`${date}T12:00:00-03:00`).getUTCDay();
              const ranges = p.hours.filter((h) => h.weekday === weekday).map((h) => {
                const [sh, sm] = h.start.split(':').map(Number);
                const [eh, em] = h.end.split(':').map(Number);
                return { top: (sh! * 60 + sm! - START_H * 60) * PX_PER_MIN, height: ((eh! * 60 + em!) - (sh! * 60 + sm!)) * PX_PER_MIN };
              });
              return (
              <section key={p.id} aria-label={`Agenda de ${p.name}`} className="min-w-64 flex-1 border-r border-border">
                <h2 className="sticky top-0 z-10 h-11 truncate border-b border-border bg-surface px-3 py-3 text-[13px] font-semibold text-fg">
                  {p.name}{ranges.length === 0 && <span className="ml-2 text-[11.5px] font-normal text-fg-subtle">não atende neste dia</span>}
                </h2>
                {/* fora do expediente fica hachurado; o horário de atendimento, em branco */}
                <div className="relative bg-[repeating-linear-gradient(135deg,transparent_0_6px,var(--dnx-border)_6px_7px)]" style={{ height: (END_H - START_H) * 60 * PX_PER_MIN }}>
                  {ranges.map((r, i) => <div key={i} className="absolute inset-x-0 bg-surface" style={{ top: r.top, height: r.height }} aria-hidden />)}
                  {Array.from({ length: END_H - START_H }, (_, i) => (
                    <div key={i} className="absolute inset-x-0 border-t border-border/70" style={{ top: i * 60 * PX_PER_MIN }} />
                  ))}
                  {isToday && nowTop > 0 && (
                    <div className="absolute inset-x-0 z-[5] border-t-2 border-danger" style={{ top: nowTop }} aria-label="Agora">
                      <span className="absolute -top-1.5 -left-1 size-3 rounded-full bg-danger" />
                    </div>
                  )}
                  {visible.filter((a) => a.professional?.id === p.id).map((a) => <Block key={a.id} a={a} onOpen={() => go({ consulta: a.id })} />)}
                </div>
              </section>
              );
            })}
          </div>
        </div>
      )}
      <AppointmentDrawer appt={selected} canWrite={canWrite} onClose={() => go({ consulta: undefined })} />
    </div>
  );
}
